/**
 * Bitmap thumbnail generation for design materials.
 * @module
 */
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { radianToDegree } from "common/utils/math";
import * as THREE from "three";
import { lightIntensityToThreePhysicalLight, scaleBufferGeometryUVs, translateBufferGeometryUVs } from "../convert";
import { BUILDING_EXTERIOR_LAYER } from "../layerConstants";
import { assignToBuildingExteriorLayer } from "../layers";
import { WHITE_BOX_PRESET } from "../sceneLights";
import { getPmremGenerator } from "../sharedPmremGenerator";
import { AOAlgorithm } from "../threejs/higharc/AOAlgorithm";
import { setLightProbeUniformsFromGridData } from "../threejs/higharc/LayerSpecificUniforms";
import { SHLightProbeGridData } from "../threejs/higharc/SHLightProbeGridData";
import type { ImageReadyCallback, SSFXImageRenderer } from "../threejs/higharc/SSFXImageRenderer";
import { type RendererParams } from "../threejs/higharc/SSFXRendererParams";
import SSFXRendererScene from "../threejs/higharc/SSFXRendererScene";
import type { ExtendedScene } from "../threejs/three-extensions";
import { getDesignMaterialInThumbnailMode } from "./designMaterialThumbnailMode";
import type { DesignMaterialParamsPreprocessed } from "./designMaterialTypes";
import type { BakedColorTexture } from "./designMaterialTypes";
import { frostedGlassUniformsFromPMREM } from "./shaders/frostedGlassTransmission";
import { PALETTE_THUMBNAIL_BG_COLOR } from "./thumbnailStyle";

const planeGeometry = new THREE.PlaneGeometry(500, 500, 1, 1);
translateBufferGeometryUVs(planeGeometry, -0.5, -0.5);
scaleBufferGeometryUVs(planeGeometry, 500);
planeGeometry.userData.isCached = true;

// Sphere geometry for glass/reflective materials - provides varied reflection vectors
const sphereGeometry = new THREE.SphereGeometry(50, 64, 32);
scaleBufferGeometryUVs(sphereGeometry, 50);
sphereGeometry.userData.isCached = true;

const sharedCamera = new THREE.OrthographicCamera();
const sharedPerspectiveCamera = new THREE.PerspectiveCamera(45, 1, 1, 1000);

export interface ThumbnailLightOptions {
  directionalLightColor: string;
  directionalLightIntensity: number;

  ambientLightColor: string;
  ambientLightIntensity: number;

  gradientTopColor: string;
  gradientMiddleColor: string;
  gradientBottomColor: string;
}

export interface ThumbnailOptions extends Partial<ThumbnailLightOptions> {
  width: number;
  height: number;
  worldWidth: number;

  samples?: number;
  /** Use a sphere geometry with perspective camera for glass/reflective materials */
  useReflectionSphere?: boolean;
  /** @deprecated only to be used in tests */
  generateRefractionProbe?: boolean;
}

export const defaultThumbnailLightOptions: ThumbnailLightOptions = {
  directionalLightColor: "#fdfdfd",
  directionalLightIntensity: 0.7,

  ambientLightColor: "#fdfdfd",
  ambientLightIntensity: 0.15,

  gradientTopColor: "#c1c1c1",
  gradientMiddleColor: "#a3a3a3",
  gradientBottomColor: "#000000",
};

class DesignMaterialThumbnailSetup {
  private scene: SSFXRendererScene;
  private container: THREE.Object3D;
  private directionalLight: THREE.DirectionalLight;
  private ambientLight: THREE.AmbientLight;
  private plane: THREE.Mesh;
  private sphere: THREE.Mesh;
  private lightProbeGridData: SHLightProbeGridData;
  private envPmrem: THREE.WebGLRenderTarget | null = null;

  // These are the colors currently stored in the light probe texture.
  private gradientTopColor = new THREE.Color(defaultThumbnailLightOptions.gradientTopColor);
  private gradientMiddleColor = new THREE.Color(defaultThumbnailLightOptions.gradientMiddleColor);
  private gradientBottomColor = new THREE.Color(defaultThumbnailLightOptions.gradientBottomColor);

  constructor() {
    const scene = new THREE.Scene();
    this.scene = new SSFXRendererScene({ scene });
    (scene as ExtendedScene).getUniformsForLayerMask = (layers, material, forceRefresh) => {
      const uniforms = this.scene.layerSpecificUniforms.getUniformsForLayerMask(layers, material, forceRefresh);
      setLightProbeUniformsFromGridData(uniforms, this.lightProbeGridData);
      if (this.envPmrem) {
        const probePos = new THREE.Vector3(0, 0, 0);
        // This is much larger than typical prism that we usually use for rendering room refractions, but it doesn't matter for shader testing.
        const simulatedPrismHalfSize = new THREE.Vector3(1000, 1000, 1000);
        const pmremUniforms = frostedGlassUniformsFromPMREM(this.envPmrem, probePos, simulatedPrismHalfSize);
        return { ...uniforms, ...pmremUniforms };
      }
      return uniforms;
    };
    this.scene.sceneLightsManager.toneMappingEnabled = false;
    this.scene.scene.background = new THREE.Color(0xaa00aa); // Purple background for debugging

    this.lightProbeGridData = new SHLightProbeGridData();
    this.lightProbeGridData.initAsSimpleGradient(
      6,
      this.gradientTopColor,
      this.gradientMiddleColor,
      this.gradientBottomColor,
    );

    this.container = new THREE.Object3D();
    this.container.castShadow = true;
    this.container.receiveShadow = true;
    this.scene.scene.add(this.container);

    const parent = this.container;
    const includeHelpers = false;

    this.directionalLight = new THREE.DirectionalLight(0xfafafa, lightIntensityToThreePhysicalLight(0.7));
    this.directionalLight.position.set(1600, -1500, 1000);
    this.directionalLight.target.position.set(0, 0, 0);
    parent.add(this.directionalLight);
    parent.add(this.directionalLight.target);
    this.directionalLight.updateMatrixWorld();

    // Set up the directional light so that it will have an empty shadow map.
    // We need the shadow map so that we can avoid extra material compilation.
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.mapSize.x = 16;
    this.directionalLight.shadow.mapSize.y = 16;
    this.directionalLight.shadow.camera.layers.disableAll();
    this.directionalLight.shadow.camera.up.set(0, 0, 1);
    this.directionalLight.shadow.camera.left = -0.1;
    this.directionalLight.shadow.camera.right = 0.1;
    this.directionalLight.shadow.camera.top = 0.1;
    this.directionalLight.shadow.camera.bottom = -0.1;
    this.directionalLight.shadow.camera.near = 50;
    this.directionalLight.shadow.camera.far = 5000;
    this.directionalLight.shadow.needsUpdate = true;
    this.directionalLight.layers.set(BUILDING_EXTERIOR_LAYER);

    this.ambientLight = new THREE.AmbientLight(0xd7d7d7, lightIntensityToThreePhysicalLight(0.15));
    parent.add(this.ambientLight);
    this.ambientLight.layers.set(BUILDING_EXTERIOR_LAYER);

    this.plane = new THREE.Mesh(planeGeometry, new THREE.MeshBasicMaterial({ color: 0x77ff77 }));
    this.plane.castShadow = true;
    this.plane.receiveShadow = true;
    parent.add(this.plane);
    // TODO: For some reason a directional light shadow map related square artifact appears near the scene origin.
    // We just move the plane enough towards the camera so that it's not visible. It could be nice to get to the root cause solved instead.
    this.plane.position.set(0, 10, 0);
    this.plane.rotation.set(Math.PI * 0.5, 0, 0);
    this.plane.updateMatrixWorld();
    assignToBuildingExteriorLayer(this.plane, false);

    // Sphere for glass/reflective materials - gives varied reflection vectors
    this.sphere = new THREE.Mesh(sphereGeometry, new THREE.MeshBasicMaterial({ color: 0x77ff77 }));
    this.sphere.castShadow = true;
    this.sphere.receiveShadow = true;
    this.sphere.visible = false; // Hidden by default, enabled when useReflectionSphere is true
    this.sphere.position.set(0, 0, 0);
    parent.add(this.sphere);
    assignToBuildingExteriorLayer(this.sphere, false);

    if (includeHelpers) {
      const helper = new THREE.CameraHelper(sharedCamera);
      parent.add(helper);

      const lightHelper = new THREE.DirectionalLightHelper(this.directionalLight, 5);
      parent.add(lightHelper);
    }
  }

  /**
   * Initialize the environment map for glass/reflective materials.
   * Uses SceneLightsManager to load the whitebox environment map (same one used in showroom/studio).
   */
  async initEnvMap(): Promise<void> {
    await this.scene.sceneLightsManager.setEnvMap(WHITE_BOX_PRESET.exteriorEnvMap, WHITE_BOX_PRESET.exteriorEnvMapEXR);
  }

  private updateLights(userOptions: Partial<ThumbnailLightOptions>) {
    const options: ThumbnailLightOptions = {
      ...defaultThumbnailLightOptions,
      ...userOptions,
    };
    this.directionalLight.color.set(options.directionalLightColor);
    this.directionalLight.intensity = lightIntensityToThreePhysicalLight(options.directionalLightIntensity);
    this.ambientLight.color.set(options.ambientLightColor);
    this.ambientLight.intensity = lightIntensityToThreePhysicalLight(options.ambientLightIntensity);

    const gradientTopColor = new THREE.Color(options.gradientTopColor);
    const gradientMiddleColor = new THREE.Color(options.gradientMiddleColor);
    const gradientBottomColor = new THREE.Color(options.gradientBottomColor);

    // Light probe texture is only updated when needed for efficiency.
    if (
      !gradientTopColor.equals(this.gradientTopColor) ||
      !gradientMiddleColor.equals(this.gradientMiddleColor) ||
      !gradientBottomColor.equals(this.gradientBottomColor)
    ) {
      this.gradientTopColor = gradientTopColor;
      this.gradientMiddleColor = gradientMiddleColor;
      this.gradientBottomColor = gradientBottomColor;
      this.lightProbeGridData.initAsSimpleGradient(
        6,
        this.gradientTopColor,
        this.gradientMiddleColor,
        this.gradientBottomColor,
      );
    }
  }

  setMaterial(
    material: THREE.Material,
    renderer: THREE.WebGLRenderer,
    useThumbnailMode: boolean,
    useReflectionSphere: boolean = false,
    generateRefractionProbe: boolean = false,
  ) {
    if (useThumbnailMode) {
      // Gray background for transparent materials, purple for debugging opaque ones.
      // Use material.transparent directly since userData.dmTransparent may not be set correctly.
      const isTransparent = material.transparent || material.userData.dmTransparent === true;
      const color: string = isTransparent ? PALETTE_THUMBNAIL_BG_COLOR : "#aa00aa";
      this.scene.scene.background = new THREE.Color(color);
    } else {
      this.scene.scene.background = null; // Transparent background for baking.
    }

    if (useThumbnailMode) {
      material = getDesignMaterialInThumbnailMode(material);
      material.needsUpdate = true;
    }

    if (generateRefractionProbe) {
      if (this.scene.environment === null) {
        throw new Error("Environment map is not loaded");
      } else {
        // Note that this is only intended for use in tests.
        this.envPmrem = getPmremGenerator(renderer).fromCubemap(this.scene.environment as THREE.CubeTexture);
      }
    }

    // Switch between plane and sphere geometry based on useReflectionSphere
    if (useReflectionSphere) {
      this.plane.visible = false;
      this.sphere.visible = true;
      this.sphere.material = material;
    } else {
      this.plane.visible = true;
      this.sphere.visible = false;
      this.plane.material = material;

      if (
        useThumbnailMode &&
        material.userData.materialParams !== undefined &&
        (material.userData.materialParams as DesignMaterialParamsPreprocessed).thumbnailDiagonalCamera
      ) {
        this.plane.rotation.set(Math.PI * 0.35, 0, Math.PI * 0.15);
      } else {
        this.plane.rotation.set(Math.PI * 0.5, 0, 0);
      }
      this.plane.updateMatrixWorld();
    }
  }

  renderImage(
    imageRenderer: SSFXImageRenderer,
    renderParams: RendererParams,
    options: Omit<ThumbnailOptions, "worldWidth">,
    camera: THREE.Camera,
    onImageReady?: ImageReadyCallback,
  ) {
    this.updateLights(options);
    return imageRenderer.renderImageSync(
      this.scene,
      camera,
      renderParams,
      {
        width: options.width,
        height: options.height,
        ssaaSamples: options.samples ?? 4,
      },
      onImageReady,
    );
  }
}

let thumbnailSetup: DesignMaterialThumbnailSetup;
let envMapInitPromise: Promise<void> | undefined;

/**
 * Ensures the thumbnail renderer has the environment map loaded.
 * Must be called before designMaterialRenderImage for glass/reflective materials.
 */
export async function ensureThumbnailEnvMapLoaded(): Promise<void> {
  if (thumbnailSetup === undefined) {
    thumbnailSetup = new DesignMaterialThumbnailSetup();
  }
  if (envMapInitPromise === undefined) {
    envMapInitPromise = thumbnailSetup.initEnvMap();
  }
  await envMapInitPromise;
}

export function designMaterialThumbnailOrthographicCamera(
  camera: THREE.OrthographicCamera,
  options: {
    worldWidth: number;
    worldHeight: number;
    worldCenterX?: number;
    worldCenterY?: number;
  },
) {
  camera.position.set(0, -100, 0);
  camera.up.set(0, 0, 1);
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld();

  camera.layers.enableAll();

  camera.near = 50;
  camera.far = 200;
  camera.left = (options.worldCenterX ?? 0) - options.worldWidth * 0.5;
  camera.right = (options.worldCenterX ?? 0) + options.worldWidth * 0.5;
  camera.top = (options.worldCenterY ?? 0) + options.worldHeight * 0.5;
  camera.bottom = (options.worldCenterY ?? 0) - options.worldHeight * 0.5;
  camera.updateProjectionMatrix();
}

/**
 * Configures a perspective camera for sphere rendering (glass/reflective materials).
 * The perspective camera provides varied view angles across the sphere surface,
 * resulting in varied reflection vectors for proper environment map sampling.
 *
 * Camera is positioned at an angle (above and to the side) so that the sphere's
 * reflections show interesting parts of the environment map (horizon, trees, ground)
 * rather than just the sky.
 */
export function designMaterialThumbnailPerspectiveCamera(
  camera: THREE.PerspectiveCamera,
  options: { width: number; height: number },
) {
  camera.aspect = options.width / options.height;
  camera.position.set(120, 60, 60);
  camera.up.set(0, 0, 1); // Z-up to make horizon horizontal in the image
  camera.lookAt(0, 0, 0);
  camera.updateMatrixWorld();

  camera.layers.enableAll();

  camera.near = 1;
  camera.far = 1000;
  camera.fov = 45;
  camera.updateProjectionMatrix();
}

export function designMaterialRenderImageOptimized(
  material: THREE.Material,
  imageRenderer: SSFXImageRenderer,
  renderParams: RendererParams,
  options: ThumbnailOptions,
  onImageReady?: ImageReadyCallback,
): string | undefined {
  renderParams.renderPipelineParams = {
    ...renderParams.renderPipelineParams,
    aoAlgorithm: AOAlgorithm.NONE, // Optimization: AO is not needed for thumbnails.
  };
  return designMaterialRenderImage(material, imageRenderer, renderParams, options, onImageReady);
}

export function designMaterialRenderImage(
  material: THREE.Material,
  imageRenderer: SSFXImageRenderer,
  renderParams: RendererParams,
  options: ThumbnailOptions,
  onImageReady?: ImageReadyCallback,
): string | undefined {
  if (thumbnailSetup === undefined) {
    thumbnailSetup = new DesignMaterialThumbnailSetup();
  }

  const useReflectionSphere = options.useReflectionSphere ?? false;
  const generateRefractionProbe = options.generateRefractionProbe ?? false;
  thumbnailSetup.setMaterial(material, imageRenderer.renderer, true, useReflectionSphere, generateRefractionProbe);

  let camera: THREE.Camera;
  if (useReflectionSphere) {
    // Use perspective camera for sphere rendering (glass/reflective materials)
    designMaterialThumbnailPerspectiveCamera(sharedPerspectiveCamera, options);
    camera = sharedPerspectiveCamera;
  } else {
    // Use orthographic camera for plane rendering (default materials)
    const worldHeight = (options.worldWidth * options.height) / options.width;
    designMaterialThumbnailOrthographicCamera(sharedCamera, { worldWidth: options.worldWidth, worldHeight });
    camera = sharedCamera;
  }

  return thumbnailSetup.renderImage(imageRenderer, renderParams, options, camera, onImageReady);
}

export function designMaterialBake({
  material,
  imageRenderer,
  renderParams,
  options,
  camera,
}: {
  material: THREE.Material;
  imageRenderer: SSFXImageRenderer;
  renderParams: RendererParams;
  options: Omit<ThumbnailOptions, "worldWidth">;
  camera: THREE.OrthographicCamera;
}): BakedColorTexture {
  if (thumbnailSetup === undefined) {
    thumbnailSetup = new DesignMaterialThumbnailSetup();
  }
  thumbnailSetup.setMaterial(material, imageRenderer.renderer, false);
  let bakedTexture: BakedColorTexture | undefined;
  thumbnailSetup.renderImage(imageRenderer, renderParams, options, camera, (image) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(image, 0, 0);
    const texture = new THREE.CanvasTexture(
      canvas,
      THREE.UVMapping,
      THREE.RepeatWrapping,
      THREE.RepeatWrapping,
      THREE.LinearFilter,
      THREE.LinearMipMapLinearFilter,
    );
    texture.generateMipmaps = true;
    texture.anisotropy = 8;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.repeat = new THREE.Vector2(1 / (camera.right - camera.left), 1 / (camera.top - camera.bottom));

    bakedTexture = {
      texture,
      worldWidth: camera.right - camera.left,
      worldHeight: camera.top - camera.bottom,
      rotation: radianToDegree(camera.rotation.z),
      loadedPromise: Promise.resolve(),
    };
  });
  assertIsNotNullOrUndefined(bakedTexture);
  return bakedTexture;
}

export function designMaterialBakeToBlob({
  material,
  imageRenderer,
  renderParams,
  options,
  camera,
}: {
  material: THREE.Material;
  imageRenderer: SSFXImageRenderer;
  renderParams: RendererParams;
  options: Omit<ThumbnailOptions, "worldWidth">;
  camera: THREE.OrthographicCamera;
}): Promise<Blob> {
  if (thumbnailSetup === undefined) {
    thumbnailSetup = new DesignMaterialThumbnailSetup();
  }
  thumbnailSetup.setMaterial(material, imageRenderer.renderer, false);
  return new Promise<Blob>((resolve, reject) => {
    thumbnailSetup.renderImage(imageRenderer, renderParams, options, camera, (image) => {
      image.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject();
        }
      }, "image/png");
    });
  });
}
